import { useQuery } from "@tanstack/react-query"
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react"
import * as React from "react"
import { Link, useNavigate, useParams } from "react-router"

import { EmptyState, SkeletonText } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useCurrentUser } from "@/features/auth"
import { canSetMarkScope } from "@/lib/roles"

import { evalLabelQuery, evalLabelsQuery, evalRemarksQuery, evalRunQuery } from "./api"
import { neighbours } from "./eval-runs-data"
import { ItemPanel, MarkDetail } from "./label/item-panel"
import { MasterPane } from "./label/master-pane"
import { FollowSwitch, PdfViewSwitch } from "./label/pdf-view-switch"
import { anchorOf, type Selection } from "./label/selection"
import { PdfViewer } from "./label/pdf-viewer"
import { BoxCard, Remarks, remarkTargetOf } from "./label/remarks"
import { usePdfDocument } from "./label/use-pdf-document"
import { hasScopeFigures, visibleMarks } from "./scope"
import { ScopeSwitch } from "./scope-controls"
import { useFollowLabel, usePdfView } from "./use-pdf-view-preference"
import { useExcludeOutOfScope } from "./use-scope-preference"

// One label of an evaluation run: the PDF the expert marked up, every mark boxed in the colour of
// how the review answered it, our findings dashed, and a remark thread on any box. The master it was
// compared against can be shown instead, or beside it.

export function EvalLabelPage() {
  const params = useParams()
  const runId = Number(params["runId"])
  const mid = params["mid"] ?? ""
  const role = useCurrentUser()?.role ?? null
  const navigate = useNavigate()
  const label = useQuery(evalLabelQuery(runId, mid))
  const labels = useQuery(evalLabelsQuery(runId))
  const remarks = useQuery(evalRemarksQuery(runId, mid))
  // The run says whether its marks were scored in scope; until it answers, the switch is held off.
  const run = useQuery(evalRunQuery(runId))
  const scoped = run.data ? hasScopeFigures(run.data.summary) : false
  const [preferExclude, setExclude] = useExcludeOutOfScope()
  const exclude = preferExclude && scoped
  // With the exclusion preferred, which marks show is unknown until the run says whether it was
  // scored in scope: showing every mark meanwhile would print a list that then shrinks.
  const marksKnown = !preferExclude || !run.isPending
  const hasPdf = label.data?.hasPdf ?? false
  const pdf = usePdfDocument(runId, mid, hasPdf)
  const [page, setPage] = React.useState(0)
  const [selection, setSelection] = React.useState<Selection>(null)
  const [view, setView] = usePdfView()
  const [follow, setFollow] = useFollowLabel()
  // Bumped on every selection, so selecting a mark re-syncs the master even when the label is
  // already on its page and the reader has paged the master elsewhere.
  const [selections, setSelections] = React.useState(0)

  // A new label opens on its first page with nothing selected.
  React.useEffect(() => {
    setPage(0)
    setSelection(null)
  }, [mid])

  const allMarks = label.data?.marks ?? []
  const marks = marksKnown ? visibleMarks(allMarks, exclude) : []
  const findings = label.data?.findings ?? []
  // Looked up over every mark, not the visible ones: a mark an admin has just ruled out of scope
  // leaves the list on the refetch, and its detail (with the decision just made) stays open.
  const selectedMark =
    selection?.kind === "mark" ? allMarks.find((m) => m.id === selection.id) : undefined
  const selectedFinding =
    selection?.kind === "finding"
      ? findings.find((f) => f.id === selection.id)
      : selectedMark?.findingId != null
        ? findings.find((f) => f.id === selectedMark.findingId)
        : undefined

  // The card opens beside a box on a painted page. A selection with no box (a mark with no
  // rectangle, a whole-document finding), or any selection while no page is shown (no PDF, or
  // one loading or failed), has nowhere to open it, so its thread is offered in the panel instead.
  const cardShown = hasPdf && pdf.status === "ready" && view !== "master"
  const unanchored =
    selection && (!cardShown || !anchorOf(selection, marks, findings))
      ? remarkTargetOf(selectedMark, selection.kind === "finding" ? selectedFinding : undefined)
      : null

  const remarkCounts = React.useMemo(() => {
    const counts = new Map<string, number>()
    for (const r of remarks.data ?? []) {
      const key = `${r.targetKind}:${r.targetRef}`
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return counts
  }, [remarks.data])

  const select = (next: Selection) => {
    setSelection(next)
    setSelections((n) => n + 1)
    if (next?.kind === "mark") {
      const mark = allMarks.find((m) => m.id === next.id)
      if (mark) setPage(mark.page)
    } else if (next?.kind === "finding") {
      const first = findings.find((f) => f.id === next.id)?.boxes[0]
      if (first) setPage(first.page)
    }
  }

  const order = labels.data?.map((l) => l.mid) ?? []
  const nav = neighbours(order, mid)
  const go = (target: string | null) => {
    if (target) void navigate(`/evaluations/${String(runId)}/labels/${encodeURIComponent(target)}`)
  }

  // The master page carrying the label page shown. Side by side with Follow on, the master turns to
  // it whenever the label's page changes (a page turned, or a mark selected); with no entry it
  // stays where it is and says so. Shown alone, the master opens on it.
  const pageMap = label.data?.pageMap ?? new Map<number, number>()
  const mapped = pageMap.get(page) ?? null
  const following = follow && pageMap.size > 0
  const followTo =
    following && mapped !== null
      ? { key: `label-${String(page)}-${String(selections)}`, page: mapped }
      : null
  const followNote =
    following && mapped === null ? (
      <span className="px-1 text-caption text-text-3">No matching master page</span>
    ) : null

  const labelViewer = (
    <PdfViewer
      hasPdf={hasPdf}
      pdf={pdf}
      page={page}
      setPage={setPage}
      marks={marks}
      findings={findings}
      selection={selection}
      onSelect={select}
      remarkCounts={remarkCounts}
      card={
        selection ? (
          <BoxCard
            runId={runId}
            mid={mid}
            role={role}
            mark={selectedMark}
            finding={selection.kind === "finding" ? selectedFinding : undefined}
            onClose={() => {
              setSelection(null)
            }}
          />
        ) : null
      }
    />
  )

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto px-4 py-5 sm:px-6">
      <Link
        to={`/evaluations/${String(runId)}`}
        className="inline-flex w-fit items-center gap-1 text-body-sm text-text-2 hover:text-text"
      >
        <ArrowLeft className="size-4" aria-hidden /> Back to the run
      </Link>

      {label.isError && (
        <EmptyState
          reason="error"
          title="This label could not be loaded"
          body={label.error.message}
        />
      )}
      {label.isPending && <SkeletonText lines={2} />}
      {label.isSuccess && (
        <header className="flex flex-wrap items-start gap-x-4 gap-y-3">
          <div className="min-w-0 flex-1 basis-72">
            <h1 className="text-subtitle text-text">
              <span className="font-mono">{label.data.mid}</span> · {label.data.product}
            </h1>
            <p className="mt-0.5 text-caption break-words text-text-3">
              {label.data.fileName} · {label.data.counts.caught} caught ·{" "}
              {label.data.counts.escalated} escalated · {label.data.counts.missed} missed ·{" "}
              {label.data.counts.handoffs} hand-offs
            </p>
          </div>
          <nav className="flex items-center gap-2" aria-label="Labels in this run">
            <Button
              size="sm"
              leadingIcon={<ChevronLeft />}
              disabled={!nav.prev}
              onClick={() => {
                go(nav.prev)
              }}
            >
              Previous label
            </Button>
            <span className="text-caption tabular-nums text-text-3">
              {nav.position >= 0 ? `${String(nav.position + 1)} of ${String(order.length)}` : ""}
            </span>
            <Button
              size="sm"
              trailingIcon={<ChevronRight />}
              disabled={!nav.next}
              onClick={() => {
                go(nav.next)
              }}
            >
              Next label
            </Button>
          </nav>
        </header>
      )}

      {label.isSuccess && (
        <div
          className={
            view === "side_by_side"
              ? "grid grid-cols-[minmax(0,1fr)] gap-4 2xl:grid-cols-[minmax(0,1fr)_380px]"
              : "grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_380px]"
          }
        >
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <PdfViewSwitch view={view} onView={setView} />
              {view === "side_by_side" && (
                <FollowSwitch checked={follow} onChange={setFollow} available={pageMap.size > 0} />
              )}
            </div>
            {view === "master" ? (
              <MasterPane
                key={mid}
                runId={runId}
                mid={mid}
                master={label.data.master}
                hasMasterPdf={label.data.hasMasterPdf}
                openAt={mapped}
              />
            ) : view === "side_by_side" ? (
              <div className="grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-2">
                {labelViewer}
                <MasterPane
                  key={mid}
                  runId={runId}
                  mid={mid}
                  master={label.data.master}
                  hasMasterPdf={label.data.hasMasterPdf}
                  compact
                  goTo={followTo}
                  note={followNote}
                />
              </div>
            ) : (
              labelViewer
            )}
          </div>
          <aside className="flex min-h-0 flex-col gap-4" aria-label="Marks and findings">
            {run.isSuccess && (
              <ScopeSwitch checked={preferExclude} onChange={setExclude} available={scoped} />
            )}
            {marksKnown ? (
              <ItemPanel
                marks={marks}
                hiddenCount={allMarks.length - marks.length}
                findings={findings}
                selection={selection}
                onSelect={select}
              />
            ) : (
              <div aria-busy="true">
                <SkeletonText lines={4} />
              </div>
            )}
            {selectedMark && (
              <MarkDetail
                mark={selectedMark}
                finding={selectedFinding}
                onOpenFinding={select}
                runId={runId}
                scopeMethod={run.data?.markScopeMethod ?? null}
                canEditScope={canSetMarkScope(role)}
              />
            )}
            {unanchored && (
              <Remarks
                key={`${unanchored.kind}:${unanchored.ref}`}
                runId={runId}
                mid={mid}
                role={role}
                target={unanchored}
              />
            )}
            {/* Keyed by label: the page stays mounted from one label to the next. */}
            <Remarks
              key={mid}
              runId={runId}
              mid={mid}
              role={role}
              target={{ kind: "label", ref: "", title: "this label as a whole" }}
            />
            <p className="px-1 text-caption text-text-3">
              To remark on one mark or finding, select it: its remarks open beside its box on the
              page, or here when there is no box to open them by.
            </p>
          </aside>
        </div>
      )}
    </div>
  )
}
